import { ActionIcon, Box, Button, Group, Title } from '@mantine/core'
import { useElementSize } from '@mantine/hooks'
import { motion } from 'framer-motion'
import { ArrowLeft2, ArrowRight2 } from 'iconsax-reactjs'
import { useState, type CSSProperties, type Key, type ReactNode } from 'react'

import {
  cardSliderTransition,
  slideUpAnimate,
  slideUpInitial,
  sliderDotAnimate,
  sliderDotTransition
} from '../animations'

const SLIDES_PER_VIEW = 4
const SLIDE_GAP = 24

type Props<T> = {
  title: string
  items: T[]
  isLoading: boolean
  getKey: (item: T) => Key
  renderItem: (item: T) => ReactNode
  renderSkeleton: () => ReactNode
  viewAllLabel: string
  onViewAll: () => void
}

export const CardSlider = <T,>({
  title,
  items,
  isLoading,
  getKey,
  renderItem,
  renderSkeleton,
  viewAllLabel,
  onViewAll
}: Props<T>) => {
  const { ref: sliderRef, width: sliderWidth } =
    useElementSize<HTMLDivElement>()

  const getSlidesPerView = () => {
    if (sliderWidth <= 0) return SLIDES_PER_VIEW
    if (sliderWidth < 500) return 1
    if (sliderWidth < 800) return 2

    return SLIDES_PER_VIEW
  }

  const slidesPerView = getSlidesPerView()

  const cardWidth =
    sliderWidth > 0
      ? (sliderWidth - SLIDE_GAP * (slidesPerView - 1)) / slidesPerView
      : 0
  const stepX = cardWidth + SLIDE_GAP

  const maxSlide = Math.max(0, items.length - slidesPerView)
  const hasNavigation = maxSlide > 0

  const [currentSlide, setCurrentSlide] = useState(0)
  const activeSlide = Math.min(currentSlide, maxSlide)

  const nextSlide = () =>
    setCurrentSlide((prev) => Math.min(prev + 1, maxSlide))
  const prevSlide = () => setCurrentSlide((prev) => Math.max(prev - 1, 0))

  return (
    <motion.div
      initial={slideUpInitial}
      animate={slideUpAnimate}
      transition={cardSliderTransition}
    >
      <Box mt="xl" pt="xl">
        <Group justify="space-between" mb="xl">
          <Title order={2}>{title}</Title>
          <Button
            variant="subtle"
            color="dark"
            rightSection={<ArrowRight2 size={18} />}
            onClick={onViewAll}
          >
            {viewAllLabel}
          </Button>
        </Group>

        <Box pos="relative" px={hasNavigation ? { base: 0, md: 52 } : 0}>
          {hasNavigation && activeSlide > 0 && (
            <Box pos="absolute" left={0} top="50%" className="slider-arrow">
              <ActionIcon
                variant="filled"
                color="dark"
                size="xl"
                radius="xl"
                onClick={prevSlide}
              >
                <ArrowLeft2 size={24} />
              </ActionIcon>
            </Box>
          )}

          <Box ref={sliderRef} className="slider-overflow">
            <div
              className="slider-track"
              style={
                {
                  '--slide-x': `-${Math.round(activeSlide * stepX)}px`
                } as CSSProperties
              }
            >
              {isLoading
                ? Array.from(
                    { length: slidesPerView },
                    (_, i) => `skeleton-${i}`
                  ).map((id) => (
                    <Box
                      key={id}
                      w={cardWidth > 0 ? cardWidth : undefined}
                      className="flex-shrink-0"
                    >
                      {renderSkeleton()}
                    </Box>
                  ))
                : items.map((item) => (
                    <Box
                      key={getKey(item)}
                      w={cardWidth > 0 ? cardWidth : undefined}
                      className="flex-shrink-0"
                    >
                      {renderItem(item)}
                    </Box>
                  ))}
            </div>
          </Box>

          {hasNavigation && activeSlide < maxSlide && (
            <Box pos="absolute" right={0} top="50%" className="slider-arrow">
              <ActionIcon
                variant="filled"
                color="dark"
                size="xl"
                radius="xl"
                onClick={nextSlide}
              >
                <ArrowRight2 size={24} />
              </ActionIcon>
            </Box>
          )}
        </Box>

        {hasNavigation && (
          <Group justify="center" mt="xl" gap="xs">
            {Array.from({ length: maxSlide + 1 }, (_, i) => `dot-${i}`).map(
              (dotId, index) => (
                <motion.button
                  key={dotId}
                  className="slide-dot"
                  onClick={() => setCurrentSlide(index)}
                  animate={sliderDotAnimate(activeSlide === index)}
                  transition={sliderDotTransition}
                />
              )
            )}
          </Group>
        )}
      </Box>
    </motion.div>
  )
}
